import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AdminSidebar from '../components/AdminSidebar';
import { getAllOrders } from '../utils/api';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, DollarSign, ShoppingCart, Calendar } from 'lucide-react';
import { toast } from 'sonner';

const AdminRevenue = () => {
  const [loading, setLoading] = useState(true);
  const [dailyData, setDailyData] = useState([]);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    completedOrders: 0,
    averageOrderValue: 0,
    todayRevenue: 0
  });

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      try {
        const response = await getAllOrders();
        const allOrders = response.data;
        processRevenueData(allOrders);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
        toast.error('Failed to load revenue data');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const processRevenueData = (allOrders) => {
    // Get last 7 days
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);
      last7Days.push(date);
    }

    // Process daily data
    const dailyStats = last7Days.map(date => {
      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);
      
      const dayOrders = allOrders.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate >= date && orderDate < nextDay;
      });

      const completedOrders = dayOrders.filter(o => o.status === 'completed');
      const revenue = completedOrders.reduce((sum, o) => sum + o.total_price, 0);
      const orderCount = completedOrders.length;

      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullDate: date.toISOString().split('T')[0],
        revenue: revenue,
        orders: orderCount,
        avgOrder: orderCount > 0 ? revenue / orderCount : 0
      };
    });

    setDailyData(dailyStats);

    // Calculate overall stats
    const completedOrders = allOrders.filter(o => o.status === 'completed');
    const totalRevenue = completedOrders.reduce((sum, o) => sum + o.total_price, 0);
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    const todayOrders = completedOrders.filter(o => {
      const orderDate = new Date(o.created_at);
      orderDate.setHours(0, 0, 0, 0);
      return orderDate.getTime() === todayDate.getTime();
    });
    const todayRevenue = todayOrders.reduce((sum, o) => sum + o.total_price, 0);

    setStats({
      totalRevenue,
      completedOrders: completedOrders.length,
      averageOrderValue: completedOrders.length > 0 ? totalRevenue / completedOrders.length : 0,
      todayRevenue
    });
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-primary/50 p-4">
          <p className="text-primary font-mono font-bold mb-2">{payload[0].payload.date}</p>
          <p className="text-white font-mono text-sm">Revenue: ₹{payload[0].value.toFixed(2)}</p>
          {payload[1] && <p className="text-accent font-mono text-sm">Orders: {payload[1].value}</p>}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-[#0F1219] items-center justify-center">
        <div className="text-primary font-mono text-xl">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-[#0F1219]">
      <div className="scanline-overlay" />
      <AdminSidebar />

      <div className="flex-1 ml-64 p-8 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-heading font-bold text-white mb-2 tracking-wider" data-testid="revenue-title">
            REVENUE ANALYTICS
          </h1>
          <p className="text-gray-400">Last 7 days performance metrics</p>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={<DollarSign />}
            label="Total Revenue"
            value={`₹${stats.totalRevenue.toFixed(2)}`}
            delay={0.1}
            testId="total-revenue-stat"
          />
          <StatCard
            icon={<TrendingUp />}
            label="Today's Revenue"
            value={`₹${stats.todayRevenue.toFixed(2)}`}
            delay={0.2}
            testId="today-revenue-stat"
          />
          <StatCard
            icon={<ShoppingCart />}
            label="Completed Orders"
            value={stats.completedOrders}
            delay={0.3}
            testId="completed-orders-stat"
          />
          <StatCard
            icon={<Calendar />}
            label="Avg Order Value"
            value={`₹${stats.averageOrderValue.toFixed(2)}`}
            delay={0.4}
            testId="avg-order-value-stat"
          />
        </div>

        {/* Revenue Line Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-card border border-border/50 p-8 mb-8"
        >
          <h2 className="text-2xl font-heading font-bold text-white mb-6 tracking-wider">
            DAILY REVENUE TREND
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
              <XAxis 
                dataKey="date" 
                stroke="#39FF85"
                style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
              />
              <YAxis 
                stroke="#39FF85"
                style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
              />
              <Line 
                type="monotone" 
                dataKey="revenue" 
                stroke="#39FF85" 
                strokeWidth={3}
                dot={{ fill: '#39FF85', r: 6 }}
                activeDot={{ r: 8 }}
                name="Revenue (₹)"
              />
            </LineChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Orders Bar Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-card border border-border/50 p-8 mb-8"
        >
          <h2 className="text-2xl font-heading font-bold text-white mb-6 tracking-wider">
            DAILY ORDERS VOLUME
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#4B5563" />
              <XAxis 
                dataKey="date" 
                stroke="#39FF85"
                style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
              />
              <YAxis 
                stroke="#39FF85"
                style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
              />
              <Bar 
                dataKey="orders" 
                fill="#B026FF"
                name="Orders"
              />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Daily Breakdown Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-card border border-border/50 p-8"
        >
          <h2 className="text-2xl font-heading font-bold text-white mb-6 tracking-wider">
            DAILY BREAKDOWN
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="daily-breakdown-table">
              <thead>
                <tr className="border-b border-primary/20">
                  <th className="text-left py-4 px-4 text-primary font-heading tracking-wider uppercase text-sm">Date</th>
                  <th className="text-left py-4 px-4 text-primary font-heading tracking-wider uppercase text-sm">Orders</th>
                  <th className="text-left py-4 px-4 text-primary font-heading tracking-wider uppercase text-sm">Revenue</th>
                  <th className="text-left py-4 px-4 text-primary font-heading tracking-wider uppercase text-sm">Avg Order</th>
                </tr>
              </thead>
              <tbody>
                {dailyData.map((day, index) => (
                  <tr key={day.fullDate} className="border-b border-border/30 hover:bg-primary/5 transition-colors" data-testid={`daily-row-${index}`}>
                    <td className="py-4 px-4 font-mono text-white">{day.date}</td>
                    <td className="py-4 px-4 font-mono text-accent font-bold">{day.orders}</td>
                    <td className="py-4 px-4 font-mono text-primary font-bold">₹{day.revenue.toFixed(2)}</td>
                    <td className="py-4 px-4 font-mono text-gray-400">₹{day.avgOrder.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const StatCard = ({ icon, label, value, delay, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    whileHover={{ y: -4 }}
    className="relative bg-card border border-border/50 p-6 group hover:border-primary/50 transition-colors"
    data-testid={testId}
  >
    <div className="absolute top-0 right-0 w-2 h-2 bg-primary" />
    <div className="text-primary mb-4">{icon}</div>
    <div className="text-gray-400 text-sm font-heading tracking-wider uppercase mb-2">{label}</div>
    <div className="text-3xl font-mono font-bold text-primary">{value}</div>
  </motion.div>
);

export default AdminRevenue;
